import { createSlice } from "@reduxjs/toolkit";
import { TEXT_SCALES, THEMES } from "src/constants";

var bg = window["configSite"]?.siteBg || "/static/images/bg.jpg";

const LEGACY_THEME_TO_MODE = {
  ONE_DARK: THEMES.DARK,
  UNICORN: THEMES.DARK,
};

const ALLOWED_TEXT_SCALES = [
  TEXT_SCALES.NORMAL,
  TEXT_SCALES.LARGE,
  TEXT_SCALES.XLARGE,
];

const normalizeThemeMode = (theme) => {
  if (!theme) return THEMES.LIGHT;
  if (theme === THEMES.LIGHT || theme === THEMES.DARK) return theme;
  if (LEGACY_THEME_TO_MODE[theme]) return LEGACY_THEME_TO_MODE[theme];
  return THEMES.LIGHT;
};

const normalizeTextScale = (scale) => {
  const parsed = parseFloat(scale);
  if (ALLOWED_TEXT_SCALES.includes(parsed)) return parsed;
  return TEXT_SCALES.NORMAL;
};

const getPersistedSettingsBootstrap = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedData = localStorage.getItem("settings");
    if (!storedData) return null;

    const parsed = JSON.parse(storedData);
    return {
      direction: parsed?.direction,
      responsiveFontSizes: parsed?.responsiveFontSizes,
      theme: normalizeThemeMode(parsed?.theme),
      textScale: normalizeTextScale(parsed?.textScale),
      adminMode: parsed?.adminMode,
      adminModeFor: parsed?.adminModeFor,
    };
  } catch (error) {
    return null;
  }
};

const persistedSettingsBootstrap = getPersistedSettingsBootstrap();

const MAX_LOADING_EVENTS = 80;

const resolveLoadingPayload = (payload, state, fallbackSource) => {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    return {
      isLoading:
        typeof payload.isLoading === "boolean" ? payload.isLoading : undefined,
      source: payload.source || fallbackSource,
      message: payload.message || state.loadingMessage || "Cargando...",
      timestamp: payload.timestamp || Date.now(),
      metadata: payload.metadata || null,
    };
  }

  return {
    isLoading: typeof payload === "boolean" ? payload : undefined,
    source: fallbackSource,
    message:
      typeof payload === "string"
        ? payload
        : state.loadingMessage || "Cargando...",
    timestamp: Date.now(),
    metadata: null,
  };
};

const appendLoadingEvent = (state, event) => {
  state.loadingEvents.unshift(event);
  if (state.loadingEvents.length > MAX_LOADING_EVENTS) {
    state.loadingEvents = state.loadingEvents.slice(0, MAX_LOADING_EVENTS);
  }
};

const getMostRecentActiveSource = (activeSources) => {
  const candidates = Object.entries(activeSources || {}).filter(
    ([, value]) => value && value.count > 0,
  );

  if (!candidates.length) return null;

  candidates.sort((a, b) => (b[1].lastEventAt || 0) - (a[1].lastEventAt || 0));
  return {
    source: candidates[0][0],
    payload: candidates[0][1],
  };
};

const registerLoadingStart = (state, payload, fallbackSource) => {
  const resolved = resolveLoadingPayload(payload, state, fallbackSource);
  const source = resolved.source || fallbackSource;

  state.loadingCounter += 1;
  if (state.loadingCounter === 1) {
    state.loadingStartTime = resolved.timestamp;
  }

  state.isLoading = true;
  state.loadingMessage = resolved.message;
  state.loadingLastSource = source;

  const sourceState = state.loadingActiveSources[source] || {
    count: 0,
    message: resolved.message,
    startedAt: resolved.timestamp,
    lastEventAt: resolved.timestamp,
    metadata: resolved.metadata,
  };

  sourceState.count += 1;
  sourceState.message = resolved.message;
  sourceState.lastEventAt = resolved.timestamp;
  if (!sourceState.startedAt) sourceState.startedAt = resolved.timestamp;
  if (resolved.metadata) sourceState.metadata = resolved.metadata;

  state.loadingActiveSources[source] = sourceState;

  appendLoadingEvent(state, {
    type: "start",
    source,
    message: resolved.message,
    at: resolved.timestamp,
    counter: state.loadingCounter,
    metadata: resolved.metadata,
  });
};

const registerLoadingStop = (state, payload, fallbackSource) => {
  const resolved = resolveLoadingPayload(payload, state, fallbackSource);
  const source = resolved.source || state.loadingLastSource || fallbackSource;

  state.loadingCounter = Math.max(0, state.loadingCounter - 1);

  const sourceState = state.loadingActiveSources[source];
  if (sourceState) {
    sourceState.count = Math.max(0, sourceState.count - 1);
    sourceState.lastEventAt = resolved.timestamp;
    if (sourceState.count === 0) {
      delete state.loadingActiveSources[source];
    } else {
      state.loadingActiveSources[source] = sourceState;
    }
  }

  state.loadingLastSource = source;

  appendLoadingEvent(state, {
    type: "stop",
    source,
    message: resolved.message,
    at: resolved.timestamp,
    counter: state.loadingCounter,
    metadata: resolved.metadata,
  });

  if (state.loadingCounter === 0) {
    state.isLoading = false;
    state.loadingStartTime = null;
    return;
  }

  const currentSource = getMostRecentActiveSource(state.loadingActiveSources);
  if (currentSource) {
    state.loadingLastSource = currentSource.source;
    state.loadingMessage =
      currentSource.payload.message || state.loadingMessage;
  }
};

const initialState = {
  ...persistedSettingsBootstrap,
  direction: persistedSettingsBootstrap?.direction || "ltr",
  responsiveFontSizes: persistedSettingsBootstrap?.responsiveFontSizes ?? true,
  settingsLoaded: false,
  theme: persistedSettingsBootstrap?.theme || THEMES.LIGHT,
  textScale: persistedSettingsBootstrap?.textScale || TEXT_SCALES.NORMAL,
  adminMode: persistedSettingsBootstrap?.adminMode ?? false,
  adminModeFor: persistedSettingsBootstrap?.adminModeFor ?? null,
  siteConfigLoaded: false,
  siteConfig: null,
  isLoading: false,
  loadingCounter: 0,
  loadingStartTime: null,
  loadingMessage: "Cargando...",
  loadingLastSource: null,
  loadingActiveSources: {},
  loadingEvents: [],
  loadingMessageQueue: [],
  hideNavbar: false,
  groupStatColors: {},
  statsDefaults: {
    dateRange: "last_year",
    addFundMonitorShowGraph: true,
    addFundMonitorNumColumns: 1,
    addFundMonitorCurrency: "current",
    addFundMonitorDate: null,
    addFundMonitorConversionRate: 1,
    addFundMonitorFiscalYear: null,
    addFundMonitorPinnedPanels: [],
  },
  csrf: null,
  open: false,
  config: {
    updated: null,
    modules: null,
    maintenance: false,
    login_bg: bg,
  },
  sections: [
    {
      subheader: "Reports",
      items: [
        {
          title: "Inicio",
          icon: "home",
          //   icon: NAVICONS['home'],
          href: "/app/",
        },
      ],
    },
  ],
};
const slice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setSettings: (state, action) => {
      const { settings } = action.payload;
      const mergedSettings = {
        ...state,
        ...settings,
      };
      const normalizedSettings = {
        ...mergedSettings,
        theme: normalizeThemeMode(mergedSettings?.theme),
        textScale: normalizeTextScale(mergedSettings?.textScale),
      };

      Object.keys(normalizedSettings).forEach((key) => {
        state[key] = normalizedSettings[key];
      });
      state.settingsLoaded = true;
      state.open = false;
      localStorage.setItem("settings", JSON.stringify(state));
    },
    setOpen: (state, action) => {
      const { open } = action.payload;
      state.open = open;
    },
    setLoaded: (state, action) => {
      const { loaded } = action.payload;

      state.settingsLoaded = loaded;
    },
    setCsrf: (state, action) => {
      const { csrf } = action.payload;
      state.csrf = csrf;
    },
    setAdminModeFor: (state, action) => {
      state.adminModeFor = action.payload;
    },
    setAdminMode: (state, action) => {
      state.adminMode = action.payload;
    },
    startLoading: (state, action) => {
      registerLoadingStart(state, action.payload, "legacy:startLoading");
    },
    stopLoading: (state, action) => {
      registerLoadingStop(state, action.payload, "legacy:startLoading");
    },
    resetLoading: (state) => {
      state.loadingCounter = 0;
      state.isLoading = false;
      state.loadingStartTime = null;
      state.loadingLastSource = null;
      state.loadingActiveSources = {};
      state.loadingEvents = [];
    },
    setIsLoading: (state, action) => {
      const payload = resolveLoadingPayload(
        action.payload,
        state,
        "legacy:setIsLoading",
      );

      if (payload.isLoading === true) {
        registerLoadingStart(state, payload, "legacy:setIsLoading");
        return;
      }

      registerLoadingStop(state, payload, "legacy:setIsLoading");
    },
    setLoadingMessage: (state, action) => {
      state.loadingMessage = action.payload;
    },
  },
});

export const reducer = slice.reducer;

// Nueva API mejorada
export const startLoading =
  (message = "Cargando...", source = "legacy:startLoading") =>
  async (dispatch) => {
    dispatch(
      slice.actions.startLoading({
        message,
        source,
        timestamp: Date.now(),
      }),
    );
  };

export const stopLoading =
  (source = "legacy:startLoading") =>
  async (dispatch, getState) => {
    const state = getState();
    const { loadingStartTime } = state.settings;

    // Garantizar tiempo mínimo de visualización (300ms)
    const MINIMUM_DISPLAY_TIME = 300;

    if (loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime;
      const remaining = Math.max(0, MINIMUM_DISPLAY_TIME - elapsed);

      if (remaining > 0) {
        // Esperar el tiempo restante antes de cerrar
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    }

    dispatch(
      slice.actions.stopLoading({
        source,
        timestamp: Date.now(),
      }),
    );
  };

export const beginGlobalLoading =
  ({ source = "global", message = "Cargando...", metadata = null } = {}) =>
  async (dispatch) => {
    dispatch(
      slice.actions.startLoading({
        source,
        message,
        metadata,
        timestamp: Date.now(),
      }),
    );
  };

export const endGlobalLoading =
  ({ source = "global", metadata = null } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const { loadingStartTime } = state.settings;
    const MINIMUM_DISPLAY_TIME = 300;

    if (loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime;
      const remaining = Math.max(0, MINIMUM_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    }

    dispatch(
      slice.actions.stopLoading({
        source,
        metadata,
        timestamp: Date.now(),
      }),
    );
  };

export const resetLoading = () => async (dispatch) => {
  dispatch(slice.actions.resetLoading());
};

// Mantener compatibilidad con API anterior
export const setIsLoading = (data) => async (dispatch) => {
  dispatch(slice.actions.setIsLoading(data));
};

export const setLoadingMessage = (data) => async (dispatch) => {
  dispatch(slice.actions.setLoadingMessage(data));
};
export const loadSiteConfig = () => async (dispatch) => {
  var _localConfig = localStorage.getItem("config");
  var defaultConfig = {
    version: 0,
  };
  if (_localConfig) {
    try {
      defaultConfig = JSON.parse(_localConfig);
    } catch (e) {
      console.log("error parsing local config", e);
      defaultConfig = {
        version: 0,
      };
    }
  }
  dispatch(
    slice.actions.setSettings({
      settings: {
        siteConfig: defaultConfig,
        siteConfigLoaded: true,
      },
    }),
  );
};
export const loadSettings = () => async (dispatch) => {
  const settings = localStorage.getItem("settings");
  if (settings) {
    const parsed = JSON.parse(settings);
    dispatch(
      slice.actions.setSettings({
        settings: {
          ...parsed,
          theme: normalizeThemeMode(parsed?.theme),
          textScale: normalizeTextScale(parsed?.textScale),
        },
      }),
    );
    dispatch(slice.actions.setLoaded({ loaded: true, open: false }));
  } else {
    localStorage.setItem("settings", JSON.stringify(initialState));
    dispatch(slice.actions.setLoaded({ loaded: true, open: false }));
  }
};

export const saveSettings = (data) => async (dispatch) => {
  dispatch(slice.actions.setSettings({ settings: data }));
};

export const setOpen = (data) => async (dispatch) => {
  dispatch(slice.actions.setOpen({ open: data }));
};

export const setAdminModeFor = (data) => async (dispatch) => {
  const settings = localStorage.getItem("settings");
  var s = {};
  if (settings) {
    s = JSON.parse(settings);
    s.adminModeFor = data;
    dispatch(slice.actions.setAdminModeFor(data));
    localStorage.setItem("settings", JSON.stringify(s));
  } else {
    s = { adminModeFor: data };
    localStorage.setItem("settings", JSON.stringify(s));
  }
};
